import * as React from 'react'
import { useReactTable, getCoreRowModel } from '../hooks/core/useReactTable'
import { useGridInitialization } from '../hooks/core/useGridInitialization'
import { useGridInitializeState } from '../hooks/utils/useGridInitializeState'
import {
  GridApiCommunity,
  GridPrivateApiCommunity,
} from '../models/api/gridApiCommunity'
import { DataGridProcessedProps } from '../models/props/DataGridProps'
import {
  densityStateInitializer,
  useGridDensity,
} from '../hooks/features/density/useGridDensity'
import {
  columnWidthOffsetStateInitializer,
  useGridColumnWidthOffset,
} from '../hooks/features/columnWidthOffset/useColumnWidthOffset'
import { useGridDimensions } from '../hooks/features/dimensions/useGridDimensions'
import {
  headerGroupDependentsStateInitializer,
  useGridHeaderGroupDependents,
} from '../hooks/features/headerGroupDependents/useHeaderGroupDependents'
import { useGridSorting } from '../hooks/features/sorting/useGridSorting'
import { useGridColumnResize } from '../hooks/features/columnResize/useGridColumnResize'
import {
  columnReorderStateInitializer,
  useGridColumnReorder,
} from '../hooks/features/columnReorder/useGridColumnReorder'
import {
  focusStateInitializer,
  useGridFocus,
} from '../hooks/features/focus/useGridFocus'
import {
  editingStateInitializer,
  useGridEditing,
} from '../hooks/features/editing/useGridEditing'
import { useGridKeyboardNavigation } from '../hooks/features/keyboardNavigation/useGridKeyboardNavigation'
import { useGridScroll } from '../hooks/features/scroll'
import { useGridColumnPinning } from '../hooks/features/columnPinning/useGridColumnPinning'
import { useGridRowSelection } from '../hooks/features/rowSelection/useGridRowSelection'
import { useGridParamsApi } from '../hooks/features/rows/useGridParamsApi'
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_COL_DEF_WITHOUT_HEADER,
} from '../colDef/gridCheckboxSelectionColDef'
import { DefaultFooterCell } from '../components/cell/defaultFooterCell'
import { useGridEvents } from '../hooks'

export const useDataGridComponent = (
  props: DataGridProcessedProps,
  inProps: any
) => {
  const privateApiRef = useGridInitialization<
    GridPrivateApiCommunity,
    GridApiCommunity
  >(inProps.apiRef)

  const publicApi = privateApiRef.current.getPublicApi()

  const columns = React.useMemo(() => {
    if (props.checkboxSelection) {
      if (props.disableSelectAll) {
        return [
          GRID_CHECKBOX_SELECTION_COL_DEF_WITHOUT_HEADER,
          // @ts-ignore
          ...props.columns,
        ]
      }
      // @ts-ignore
      return [GRID_CHECKBOX_SELECTION_COL_DEF, ...props.columns]
    } else {
      return props.columns
    }
  }, [props.checkboxSelection, props.disableSelectAll, props.columns])

  publicApi.tableInstance = useReactTable({
    ...inProps,
    defaultColumn: {
      footer: DefaultFooterCell,
      ...inProps.defaultColumn,
    },
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  })

  /**
   * Register all state initializers here.
   */
  useGridInitializeState(densityStateInitializer, privateApiRef, props)
  useGridInitializeState(
    columnWidthOffsetStateInitializer,
    privateApiRef,
    props
  )
  useGridInitializeState(
    headerGroupDependentsStateInitializer,
    privateApiRef,
    props
  )
  useGridInitializeState(editingStateInitializer, privateApiRef, props)
  useGridInitializeState(columnReorderStateInitializer, privateApiRef, props)
  useGridInitializeState(focusStateInitializer, privateApiRef, props)

  useGridKeyboardNavigation(privateApiRef, props)
  useGridParamsApi(privateApiRef)
  useGridEditing(privateApiRef, props)
  useGridFocus(privateApiRef)
  useGridDensity(privateApiRef, props)
  useGridDimensions(privateApiRef, props)
  useGridColumnWidthOffset(privateApiRef, props)
  useGridHeaderGroupDependents(privateApiRef, props)
  useGridSorting(privateApiRef)
  useGridColumnResize(privateApiRef)
  useGridScroll(privateApiRef)
  useGridColumnReorder(privateApiRef, props)
  useGridColumnPinning(privateApiRef)
  useGridRowSelection(privateApiRef, props)
  useGridEvents(privateApiRef, props)

  React.useEffect(() => {
    privateApiRef.current.unstable_updateGridDimensionsRef()
    /* eslint-disable-next-line */
  }, [props.data])

  return privateApiRef
}
